import { ChangeDetectorRef, Directive, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, map, take } from 'rxjs';

import { normalizeSearch } from '@app/hr/hr-leave-board';
import { businessDayOf } from '@app/shared/time';
import type { RentalCancellation, RentalDecisionStamp, RentalPrescription, RentalWindowDecision } from '@app/shared/_models/sales';
import type { Day, ReasonUsage, RentalLineView } from '../rental-billing.model';
import { DecisionRunResult, RentalBillingService, SimulationRequest, SimulationState, simulationKey, simulationStream, tooManyDecisions } from '../rental-billing.service';
import type { RentalDecisionDialogsService } from '../rental-decision-dialogs.service';
import type { RentalDecisionEvent } from '../rental-decision-events';
import type { CancelTarget, DecisionDialogResult } from '../rental-dialogs.contract';
import type { RentalLifecycleStore } from '../rental-lifecycle.store';
import { addMonthsClamped, firstOfMonth, frShort } from '../rental-days';
import { parseTypedDay } from '../rental-picker.geometry';

export interface ReasonChip { id: number; name: string; color: string | null; }

// Texte d'annulation des seules simulations : jamais enregistré.
const SIMULATION_REASON = 'simulation';

// Socle des dialogues du Lot 3a : vue du store, effet simulé, envoi en série puis bilan commun (C.4).
@Directive()
export abstract class RentalDecisionDialogBase<D extends { store: RentalLifecycleStore; orderId: number }> {
  readonly data: D = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<unknown, DecisionDialogResult> = inject(MatDialogRef);
  protected readonly svc = inject(RentalBillingService);
  protected readonly cdr = inject(ChangeDetectorRef);
  protected abstract readonly dialogs: RentalDecisionDialogsService;

  sim: SimulationState = { loading: false, view: null, error: null };
  saving = false;
  private nestedSaved = 0;
  private readonly requests = new BehaviorSubject<SimulationRequest>({ events: null, at: null });

  constructor() {
    this.data.store.changed$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.update();
      this.cdr.markForCheck();
    });
    this.data.store.ensureReasons();
    simulationStream(this.svc, this.data.orderId, this.requests.pipe(map((r) => ({ events: simulationEvents(r.events), at: r.at }))), this.data.store.openOnly)
      .pipe(takeUntilDestroyed())
      .subscribe((s) => {
        this.sim = s;
        this.cdr.markForCheck();
      });
  }

  // Recalcule l'écran à partir du store et de la saisie, puis demande la simulation (plan).
  protected abstract update(): void;

  get store(): RentalLifecycleStore {
    return this.data.store;
  }

  // La version de la vue entre dans la requête : après une annulation imbriquée, la même saisie se rechiffre sur le dossier rechargé.
  // Au-delà de DECISIONS_MAX, rien n'est simulé : l'erreur de tooManyDecisions est dans la liste du dialogue.
  protected plan(evs: RentalDecisionEvent[] | null): void {
    this.requests.next({ events: evs && evs.length ? evs : null, at: this.store.view?.computed_at ?? null });
  }

  // Vrai si la simulation affichée répond à ces évènements, sur la vue actuelle du dossier, sans calcul en cours ni échec.
  protected simAnswers(evs: RentalDecisionEvent[]): boolean {
    const s = this.sim;
    return !s.loading && !s.error && !!s.view && s.answers === simulationKey(simulationEvents(evs), this.store.view?.computed_at ?? null);
  }

  // La série va au bout même si le dialogue disparaît : chaque envoi accepté reçoit son rechargement et son bilan.
  // after : suite jouée seulement si tout est enregistré (fichiers joints, rattachés aux numéros rendus dans run.done), le dialogue se ferme à sa fin.
  protected send(evs: RentalDecisionEvent[], what: string, gender: 'f' | 'm' = 'f', after?: (run: DecisionRunResult) => Observable<unknown>): void {
    if (this.saving || !evs.length || tooManyDecisions(evs.length)) return;
    this.saving = true;
    this.dialogRef.disableClose = true;
    this.update();
    this.cdr.markForCheck();
    this.svc.postDecisions(evs).subscribe((run) => {
      const r = this.dialogs.settle(this.store, run, what, gender);
      const close = () => this.dialogRef.close({ saved: r.saved + this.nestedSaved, failed: r.failed });
      if (!after || r.failed || r.saved === 0) close();
      else after(run).subscribe({ complete: close, error: close });
    });
  }

  // Annulation ouverte par-dessus : le store se recharge seul, ce dialogue reste ouvert et suit.
  protected nested(result$: Observable<DecisionDialogResult>): void {
    result$.pipe(take(1)).subscribe((r) => (this.nestedSaved += r?.saved ?? 0));
  }

  close(): void {
    this.dialogRef.close({ saved: this.nestedSaved, failed: false });
  }

  // Saisie tolérante (« 28 », « 11/6/26 ») ; undefined = incomprise, signalée sur le champ.
  protected typedDay(ev: Event, leftMonth: Day | null): Day | null | undefined {
    const input = ev.target as HTMLInputElement;
    const v = parseTypedDay(input.value, (leftMonth ?? '').slice(0, 7));
    input.setCustomValidity(v === undefined ? 'Date non comprise' : '');
    if (v === undefined) input.reportValidity();
    return v;
  }
}

// Les textes libres ne changent pas l'effet : pas de nouvel appel à chaque frappe, et une annulation se chiffre avant que son motif soit saisi.
export function simulationEvents(evs: RentalDecisionEvent[] | null): RentalDecisionEvent[] | null {
  if (!evs) return null;
  return evs.map((e) => {
    const data: Record<string, unknown> = { ...e.data };
    delete data['comment'];
    delete data['reference'];
    if (data['cancel_reason'] !== undefined) data['cancel_reason'] = SIMULATION_REASON;
    return { typ: e.typ, data } as unknown as RentalDecisionEvent;
  });
}

export function lineName(l: { product_name: string | null; product: number } | null | undefined): string {
  return l ? (l.product_name ?? `article ${l.product}`) : '';
}

export function stampText(store: RentalLifecycleStore, s: RentalDecisionStamp | null | undefined): string {
  if (!s) return '';
  const day = businessDayOf(s.dat);
  return `saisie par ${store.userName(s.id_user)}${day ? ' le ' + frShort(day) : ''}`;
}

export function cancelledText(store: RentalLifecycleStore, c: RentalCancellation): string {
  const day = businessDayOf(c.dat);
  return `Annulée${day ? ' le ' + frShort(day) : ''} par ${store.userName(c.id_user)} : ${c.reason}`;
}

export function reasonChips(store: RentalLifecycleStore, usage: ReasonUsage): ReasonChip[] {
  return store.reasonsFor(usage).map((r) => ({ id: r.id ?? 0, name: r.name ?? `motif ${r.id}`, color: store.reasonColor(r.id ?? 0) }));
}

// Mois de gauche qui montre le jour : inchangé s'il est déjà dans les mois affichés.
export function monthShowing(leftMonth: Day | null, day: Day, count = 2): Day {
  if (leftMonth && day >= leftMonth && day < addMonthsClamped(leftMonth, count)) return leftMonth;
  return firstOfMonth(day);
}

// Clés d'identité des bornes et prescriptions posées ensemble sur un mémo (dialogues et fiche) ; prescription : start et end inclus.
export const windowTwinKey = (w: RentalWindowDecision): string => JSON.stringify([w.bound, w.day, w.reason_id, w.comment?.trim() ?? '']);
export const prescriptionTwinKey = (p: RentalPrescription): string => JSON.stringify([p.start, p.end]);

// Entrées identiques non annulées des autres lignes RENTAL lisibles du même mémo : proposées décochées (E.9).
export function memoTwins<T extends { id: number; cancelled?: unknown }>(
  lines: RentalLineView[], lineId: number, id: number, pick: (l: RentalLineView) => T[] | undefined, key: (e: T) => string,
): CancelTarget[] {
  const l = lines.find((x) => x.id === lineId);
  const memo = normalizeSearch(l?.memo);
  const entry = l ? pick(l)?.find((e) => e.id === id) : undefined;
  if (!l || !entry || !memo) return [];
  const k = key(entry);
  const out: CancelTarget[] = [];
  for (const x of lines) {
    if (x.id === lineId || x.id <= 0 || x.typ !== 'RENTAL' || x.lifecycle_unreadable || !x.decisions || normalizeSearch(x.memo) !== memo) continue;
    for (const e of pick(x) ?? []) if (!e.cancelled && key(e) === k) out.push({ lineId: x.id, id: e.id, checked: false });
  }
  return out;
}
